import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Text, Button, Group, Badge, SimpleGrid } from '@repletoryreach/sdk-ui';
import {
  Alert,
  DataTable,
  EmptyState,
  FilterDrawer,
  PageHeader,
  StatusBadge,
  TableShell,
  type FilterField,
  type FilterValues
} from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { identity } from '@repletoryreach/sdk-api';
import type { Indexed } from '../../../lib/types';
import { formatDateTime, formatInteger, formatRelative } from '../../../lib/format';
import { usePersistedState } from '../../../lib/usePersistedState';
import { useTenantMap } from '../../../data/useTenants';
import { TenantName } from '../../../components/TenantName';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { StatCard } from '../../../components/StatCard';
import { SystemTabs } from '../components/SystemTabs';
import { RangePicker, rangeToSinceIso, type RangeOption } from '../../audit/components/RangePicker';
import { useT } from '../../../i18n/I18nProvider';

const MUTED = 'var(--rr-color-text-muted)';
type SecurityEvent = identity.SecurityEvent;

/**
 * A window, so the page is a question about a period rather than "the newest
 * 200 rows of all time". 500 is the endpoint's own ceiling.
 */
const PAGE_LIMIT = 200;

const icon = (path: ReactNode) => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {path}
  </svg>
);
const ShieldIcon = icon(<><path d="M10 2.8 4.5 5v4.4c0 3.4 2.3 6.3 5.5 7.8 3.2-1.5 5.5-4.4 5.5-7.8V5z" /></>);
const KeyIcon = icon(<><circle cx="7" cy="10" r="3" /><path d="M10 10h7M14.5 10v2.6M17 10v2" /></>);
const AlertIcon = icon(<><path d="M10 3.2 2.8 16h14.4z" /><path d="M10 8v3.4M10 13.6v.1" /></>);
const EyeIcon = icon(<><path d="M1.8 10S4.7 5.2 10 5.2 18.2 10 18.2 10 15.3 14.8 10 14.8 1.8 10 1.8 10z" /><circle cx="10" cy="10" r="2.2" /></>);

/** The severity the lookup row carries, mapped to the badge vocabulary. */
function toneFor(row: SecurityEvent): 'healthy' | 'warning' | 'down' | 'pending' | 'inactive' {
  if (row.outcome === 'failure') return 'down';
  if (row.severity === 'warning') return 'warning';
  if (row.severity === 'notice') return 'pending';
  return 'healthy';
}

/**
 * The platform's cross-tenant security feed: sign-ins and the attempts that
 * failed, password and second-factor changes, keys minted and revoked, and
 * every impersonation an operator started.
 *
 * Two things the page has to be honest about, because a reader cannot recover
 * either from the rows. First, `repeat_count`: a row can stand for a hundred
 * failures from one address, and a table that showed each as "1 attempt" would
 * make a credential-stuffing run look like background noise. Second,
 * `retention_days`: a quiet week and a week older than the platform keeps look
 * identical once the rows are gone, and only one of those is good news.
 */
export default function SecurityPage() {
  const t = useT();
  const nav = useNavigate();

  const [range, setRange] = usePersistedState<RangeOption>('admin.security.range', '7d');
  const since = useMemo(() => rangeToSinceIso(range), [range]);

  const [pageSize, setPageSize] = usePersistedState<number>('admin.security.pageSize', 25);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);

  const typesQ = useQuery({
    queryKey: ['admin', 'security-events', 'types'],
    queryFn: () => identity.listSecurityEventTypes(),
    staleTime: 5 * 60_000
  });

  // The type and outcome filters are bound server-side: narrowing in the
  // browser would narrow a page rather than the window, and the count under the
  // table would then describe neither.
  const eventType = (filters.event_type as string | undefined) || undefined;
  const outcome = (filters.outcome as 'success' | 'failure' | undefined) || undefined;

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['admin', 'security-events', since, eventType, outcome],
    queryFn: () => identity.listSecurityEvents({ since, event_type: eventType, outcome, limit: PAGE_LIMIT })
  });
  const { map: tenants } = useTenantMap();

  const rows = useMemo(() => (data?.data ?? []) as SecurityEvent[], [data]);
  const meta = data?.meta;
  const loaded = Boolean(data) && !error;

  // Occurrences, not rows: a folded failure row stands for `repeat_count` of
  // them, and the count an operator cares about is the second one.
  const occurrences = useMemo(() => rows.reduce((n, r) => n + (r.repeat_count || 1), 0), [rows]);
  const failures = useMemo(
    () => rows.filter((r) => r.outcome === 'failure').reduce((n, r) => n + (r.repeat_count || 1), 0),
    [rows]
  );
  const impersonations = useMemo(
    () => rows.filter((r) => r.event_type === 'impersonation_started').length,
    [rows]
  );
  const addresses = useMemo(
    () => new Set(rows.filter((r) => r.outcome === 'failure' && r.ip).map((r) => r.ip)).size,
    [rows]
  );

  const typeOptions = typesQ.data?.data ?? [];
  const filterFields: FilterField[] = useMemo(() => [
    {
      key: 'event_type',
      label: t('security.filters.eventType'),
      placeholder: t('security.filters.anyEventType'),
      options: typeOptions.map((o) => ({ value: o.code, label: o.name }))
    },
    {
      key: 'outcome',
      label: t('security.filters.outcome'),
      placeholder: t('security.filters.anyOutcome'),
      options: [
        { value: 'success', label: t('security.outcome.success') },
        { value: 'failure', label: t('security.outcome.failure') }
      ]
    }
  ], [t, typeOptions]);

  const typeLabel = (code: string) => typeOptions.find((o) => o.code === code)?.name ?? code;

  const hasActiveFilters = Boolean(query.trim()) || Object.values(filters).some(Boolean);
  function clearAllFilters() { setQuery(''); setFilters({}); setPage(1); }

  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (eventType) pills.push({ id: 'event_type', field: t('security.filters.eventType'), value: typeLabel(eventType) });
  if (outcome) pills.push({ id: 'outcome', field: t('security.filters.outcome'), value: t(`security.outcome.${outcome}`) });

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') return setQuery('');
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  // Only the free-text search runs in the browser, over the page already
  // fetched, and the footer says so.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.user_email ?? '').toLowerCase().includes(q) ||
      (r.ip ?? '').toLowerCase().includes(q) ||
      (r.detail ?? '').toLowerCase().includes(q) ||
      (r.device ?? '').toLowerCase().includes(q) ||
      (r.actor_email ?? '').toLowerCase().includes(q) ||
      r.event_type.toLowerCase().includes(q) ||
      (r.organisation_id ? (tenants.get(r.organisation_id)?.name ?? '').toLowerCase().includes(q) : false)
    );
  }, [rows, query, tenants]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('system.title')}
        title={t('security.title')}
        description={t('security.pageDescription')}
        actions={
          <Group gap="sm" wrap="nowrap">
            <RangePicker value={range} onChange={(next) => { setRange(next); setPage(1); }} />
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
              {t('action.refresh')}
            </Button>
          </Group>
        }
      />

      <SystemTabs />

      {/* The window this page can see. Said out loud, because a quiet month and
          a month the platform no longer keeps look exactly the same. */}
      {meta?.retention_days != null && (
        <Text role="caption" c={MUTED}>
          {t('security.retentionNote', { days: formatInteger(meta.retention_days) })}
        </Text>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <StatCard
          loading={isLoading}
          tone="primary"
          icon={ShieldIcon}
          label={t('security.metrics.events')}
          value={loaded ? formatInteger(occurrences) : t('common.notAvailable')}
          hint={loaded ? t('security.metrics.eventsHint', { rows: formatInteger(rows.length) }) : undefined}
        />
        <StatCard
          loading={isLoading}
          tone="error"
          icon={AlertIcon}
          label={t('security.metrics.failures')}
          value={loaded ? formatInteger(failures) : t('common.notAvailable')}
          hint={loaded ? t('security.metrics.failuresHint', { addresses: formatInteger(addresses) }) : undefined}
        />
        <StatCard
          loading={isLoading}
          tone="warning"
          icon={EyeIcon}
          label={t('security.metrics.impersonations')}
          value={loaded ? formatInteger(impersonations) : t('common.notAvailable')}
          hint={t('security.metrics.impersonationsHint')}
        />
        <StatCard
          loading={isLoading}
          tone="info"
          icon={KeyIcon}
          label={t('security.metrics.window')}
          value={loaded && meta ? formatInteger(meta.total) : t('common.notAvailable')}
          hint={t('security.metrics.windowHint')}
        />
      </SimpleGrid>

      {/* A window that holds more than one page of rows says so, rather than
          letting the newest 200 stand in for the whole period. */}
      {loaded && meta && meta.total > rows.length && (
        <Alert color="info" title={t('security.capped.title')}>
          {t('security.capped.body', {
            shown: formatInteger(rows.length),
            total: formatInteger(meta.total)
          })}
        </Alert>
      )}

      <TableShell
        title={t('security.table.title')}
        description={t('security.table.description')}
        totalLabel={t('security.table.totalLabel', {
          shown: formatInteger(filtered.length),
          total: formatInteger(rows.length)
        })}
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('security.table.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
          </Group>
        }
        belowToolbar={<ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <EmptyState
            title={hasActiveFilters ? t('security.empty.filteredTitle') : t('security.empty.title')}
            description={hasActiveFilters ? t('common.tryClearingFilters') : t('security.empty.description')}
            action={hasActiveFilters
              ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button>
              : undefined}
          />
        }
        error={error ? (
          <Alert color="error" title={t('security.loadError')}>
            <Stack gap="sm" align="flex-start">
              <Text role="bodySmall">{(error as Error).message}</Text>
              <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>{t('action.retry')}</Button>
            </Stack>
          </Alert>
        ) : null}
        pagination={
          <Group justify="space-between" align="center">
            <Text role="caption" c={MUTED}>
              {t('security.table.pageOf', { page: formatInteger(page), pages: formatInteger(pageCount) })}
            </Text>
            <Group gap="xs" wrap="nowrap">
              <Button size="xs" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                {t('action.previous')}
              </Button>
              <Button size="xs" variant="outline" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>
                {t('action.next')}
              </Button>
              <Button
                size="xs"
                variant="subtle"
                onClick={() => { setPageSize(pageSize === 25 ? 50 : 25); setPage(1); }}
              >
                {t('security.table.perPage', { size: formatInteger(pageSize) })}
              </Button>
            </Group>
          </Group>
        }
      >
        <DataTable<Indexed<SecurityEvent>>
          records={paged as Indexed<SecurityEvent>[]}
          loading={isLoading}
          idAccessor="id"
          emptyState={t('security.empty.title')}
          columns={[
            {
              accessor: 'occurred_at',
              title: t('security.columns.when'),
              sortable: true,
              render: (r) => (
                <span title={formatDateTime(r.occurred_at)}>{formatRelative(r.occurred_at)}</span>
              )
            },
            {
              accessor: 'event_type',
              title: t('security.columns.event'),
              render: (r) => (
                <Group gap="xs" wrap="nowrap">
                  <StatusBadge status={toneFor(r)} size="xs">{typeLabel(r.event_type)}</StatusBadge>
                  {/* A folded row stands for many occurrences. Without this the
                      table reports a thousand guesses as one attempt. */}
                  {(r.repeat_count ?? 1) > 1 && (
                    <Tooltip label={t('security.repeatTooltip', { count: formatInteger(r.repeat_count) })} position="top">
                      <Badge size="sm" variant="light" color="red">
                        {t('security.repeatBadge', { count: formatInteger(r.repeat_count) })}
                      </Badge>
                    </Tooltip>
                  )}
                </Group>
              )
            },
            {
              accessor: 'user_email',
              title: t('security.columns.account'),
              render: (r) => (
                <Stack gap={2}>
                  <Text role="bodySmall">{r.user_email ?? t('common.notAvailable')}</Text>
                  {/* An attempt against an address no account holds is the row
                      whose absence would be the loudest, so it is labelled
                      rather than left looking like an ordinary sign-in. */}
                  {r.user_id == null && (
                    <Text role="caption" c={MUTED}>{t('security.noSuchAccount')}</Text>
                  )}
                </Stack>
              )
            },
            {
              accessor: 'organisation_id',
              title: t('security.columns.tenant'),
              render: (r) => r.organisation_id
                ? <TenantName id={r.organisation_id} />
                : <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>
            },
            {
              accessor: 'ip',
              title: t('security.columns.from'),
              render: (r) => (
                <Stack gap={2}>
                  <Text role="bodySmall">{r.ip ?? t('common.notAvailable')}</Text>
                  {r.device && <Text role="caption" c={MUTED} title={r.user_agent ?? undefined}>{r.device}</Text>}
                </Stack>
              )
            },
            {
              // The actor column is the reason this page exists: it is the only
              // feed in the product that knows who did something to somebody
              // else's account.
              accessor: 'actor_email',
              title: t('security.columns.actor'),
              render: (r) => r.actor_email
                ? <Text role="bodySmall">{r.actor_email}</Text>
                : <Text role="caption" c={MUTED}>{t('security.selfService')}</Text>
            },
            {
              accessor: 'detail',
              title: t('security.columns.detail'),
              render: (r) => r.detail
                ? <Text role="bodySmall" c={MUTED}>{r.detail}</Text>
                : <Text role="caption" c={MUTED}>{t('common.notAvailable')}</Text>
            }
          ]}
          onRowClick={(r) => { if (r.organisation_id) nav(`/tenants/${r.organisation_id}`); }}
        />
      </TableShell>
    </Stack>
  );
}
